import type { EventTarget } from "@map/mapiq-map/EventTarget";
import { trackError } from "@lib/infrastructure";
import {
  createEmptyHaNMapLayer,
  getHaNMapNodeId,
  getHaNMapState,
  getHaNSelectionNodeId,
  HaNContext,
  type HaNTabMapLayer,
  HaNViewStoreContext,
  resolveTapTarget,
  selectHoveredNodeId,
  selectSelection,
  useHaNView,
} from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { MapiqMap } from "@molecules";

import { useConfirmDiscardingBooking } from "../details/useConfirmDiscardingBooking";
import { useHaNLayoutMode } from "../state/layout/useHaNLayoutMode";
import { useFitNodeInView } from "./useFitNodeInView";

/**
 * The page's one map. It stays while tabs switch, only swapping the layer, so the floor isn't rebuilt and the view
 * is kept. Draws the layer's tab selection and the hovered node.
 */
export const HaNMapFrame = ({ floorId, tabLayer }: { floorId: string; tabLayer: HaNTabMapLayer | null }) => {
  const { tab: currentTab, building, mapViewApiRef } = useRequiredContext(HaNContext);

  // Right after a floor change the previous floor's layer is still here, and the map throws on nodes it doesn't have
  const isLayerForFloor = tabLayer?.layer.floorId === floorId;
  const tab = isLayerForFloor ? tabLayer.tab : currentTab;
  const layer = isLayerForFloor ? tabLayer.layer : createEmptyHaNMapLayer(floorId);

  const viewStore = useRequiredContext(HaNViewStoreContext);
  const layoutMode = useHaNLayoutMode();
  const selection = useHaNView(selectSelection(tab));
  const hoveredNodeId = useHaNView(selectHoveredNodeId);

  const confirmDiscardingBooking = useConfirmDiscardingBooking();
  const fitNodeInView = useFitNodeInView();

  const selectedNodeId = getHaNMapNodeId(layer, selection ? getHaNSelectionNodeId(selection) : null);

  // A person picked on another floor is zoomed to when that floor's map is built. The map only reads this then.
  const defaultNodeIdsInView = selection?.kind === "person" && selection.nodeId ? [selection.nodeId] : undefined;

  const handleTap = (target: EventTarget) => {
    const tap = resolveTapTarget({
      tab,
      target,
      nodeIds: layer.nodeIds,
      selectionByNodeId: layer.selectionByNodeId,
      selectedNodeId,
    });

    if (tap.type === "ignore") return;

    const nextSelection = tap.type === "select" ? tap.selection : null;

    confirmDiscardingBooking(() => {
      viewStore.selectFromMap(tab, nextSelection, { isPhoneLayout: layoutMode === "mobile" });

      // Pans the selection into view when it's behind the filter bar or the panel that the tap opens
      const nextNodeId = nextSelection ? getHaNSelectionNodeId(nextSelection) : null;

      if (nextNodeId) fitNodeInView(nextNodeId);
    }, nextSelection?.id);
  };

  // A desk spotlights its area
  const handleHover = (target: EventTarget) => {
    viewStore.hover(target.areaId ?? target.roomId ?? target.facilityId);
  };

  if (!building.buildingId) return null;

  return (
    <div className="absolute inset-0">
      <MapiqMap
        buildingId={building.buildingId}
        floorId={floorId}
        type="preference"
        mapState={getHaNMapState(layer, selectedNodeId)}
        mapStyle={layer.mapStyle}
        markerData={layer.markerData}
        spotlightedNodeId={getHaNMapNodeId(layer, hoveredNodeId)}
        defaultNodeIdsInView={defaultNodeIdsInView}
        mapViewApiRef={mapViewApiRef}
        onTap={handleTap}
        onHover={handleHover}
        onError={error => trackError(error, { area: "HereAndNow_Map" })}
      />
    </div>
  );
};
